import React, { useEffect, useMemo, useState } from 'react';
import { Input, Select, Spin } from 'antd';
import { CaretDownFilled } from '@ant-design/icons';
import { DrawerItem } from '../Legend/LegendDrawer';
import MapPortalService from '../../../services/MapPortal/mapportal.service';
import '../components/MapView.scss';
import ViewAttributeTable from '../Legend/ViewAttributeTable';
import { useLayerProperitesContext, defaultLayerPropertiesState } from '../../../context/LayerPropertiesContext';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useQueryExtentDraw, ExtentPolygon } from '../../../hooks/useQueryExtentDraw';
import './QueryToolModal.scss';
import * as Cesium from 'cesium';
import { formatDisplayDate } from '../../../shared/utils/formatDisplayDate';
import { useAuth } from '../../../context/AuthContext';
import { MapToolAction } from '../../../enums/mapPortalActions';
import { mapToolAccess } from '../constants';

type SearchExtent = 'all' | 'polygon';

const { Option } = Select;

export const SELECT_CARET = <CaretDownFilled />;

// Sentinel for an explicitly-selected NULL value in the VALUE dropdown.
// Must stay distinct from "" (unset) and never collapse to falsy, so it
// survives the `|| undefined` / `=== ""` checks used everywhere to detect
// "no value chosen yet". Only unwrapped back to a real `null` at the two
// places that build the actual query: `toCondition` and `buildMatchCql`.
// Sentinel for a user-selected NULL in the VALUE dropdown (Ant Select can't
// use a real `null` option value). Unwrapped to JS null / SQL IS NULL when
// building sift filters and CQL. Shared with ProximityToolModal.
export const NULL_VALUE = '__NULL__';

const OPERATORS = [
  { label: 'IS', value: 'IS', numericOnly: false },
  { label: 'IS NOT', value: 'IS NOT', numericOnly: false },
  { label: 'CONTAINS', value: 'LIKE', numericOnly: false },
  { label: 'DOES NOT CONTAIN', value: 'NOT LIKE', numericOnly: false },
  { label: 'GREATER THAN', value: 'GT', numericOnly: true },
  { label: 'LESS THAN', value: 'LT', numericOnly: true },
  { label: 'GREATER THAN OR EQUAL TO', value: 'GTE', numericOnly: true },
  { label: 'LESS THAN OR EQUAL TO', value: 'LTE', numericOnly: true },
  { label: 'IS EMPTY', value: 'EMPTY', numericOnly: false },
  { label: 'IS NOT EMPTY', value: 'NOT_EMPTY', numericOnly: false },
];

export interface Filter {
  category: string;
  condition: string;
  value: string;
  logicalOperator: string;
}

interface QueryModalProps {
  data?: any;
  queryModalToolVisible: boolean;
  setQueryModalToolVisible: (value: boolean) => void;
  drawerItems: DrawerItem[];
  setDrawerItems: React.Dispatch<React.SetStateAction<any[]>>;
  queryResultModalVisible: boolean;
  setQueryResultModalVisible: (value: boolean) => void;
  settings?: {
    queryTool: { enabled: boolean; name: string; color: string };
    proximityTool: { enabled: boolean; name: string; color: string };
  };
  mapId?: string;
  viewerRef: any;
  setCurrentTool?: (value: string) => void;
  setIsLayerLoading?: (loading: boolean) => void;
  onLayerUpdateComplete?: () => Promise<void>;
}

const QueryModal: React.FC<QueryModalProps> = ({
  queryModalToolVisible,
  setQueryModalToolVisible,
  drawerItems,
  setDrawerItems,
  queryResultModalVisible,
  setQueryResultModalVisible,
  settings,
  mapId,
  viewerRef,
  setCurrentTool,
  setIsLayerLoading,
  onLayerUpdateComplete,
}) => {
  const { canAccess } = useAuth();
  const canSaveResults = canAccess(mapToolAccess(MapToolAction.SAVE_TABLE_RESULTS));

  const [filters, setFilters] = useState<Filter[]>([{ category: '', condition: '', value: '', logicalOperator: '' }]);
  const [searchExtent, setSearchExtent] = useState<SearchExtent>('all');
  const [extentPolygon, setExtentPolygon] = useState<ExtentPolygon | null>(null);
  // null => no extent filter applied (show all layers); array => only these
  // layers have features inside the drawn polygon (US-103163 dropdown filter).
  const [extentLayerNames, setExtentLayerNames] = useState<string[] | null>(null);
  const [layerName, setLayerName] = useState('');
  const [features, setFeatures] = useState<any>({});
  const [keyOptions, setKeyOptions] = useState<{ label: string; value: string }[]>([]);
  // First page of match ids only (default 500) — never the full result set.
  const [filteredItems, setFilteredItems] = useState<any>([]);
  // Total matches from the paginated analysis response (scales to millions).
  const [matchTotal, setMatchTotal] = useState(0);
  // US-103168 validation (Scenarios 19-22) + execution errors (26/27).
  const [layerError, setLayerError] = useState<string>('');
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({});
  const [submitError, setSubmitError] = useState<string>('');
  // The matched feature ids seed the result table's selection (server mode).
  // For large results we seed select-all-matches instead of enumerating ids.
  const seedSelectionIds = useMemo(() => (filteredItems ?? []).map((f: any) => f.id).filter(Boolean), [filteredItems]);
  const seedSelectAllMatches = matchTotal > seedSelectionIds.length;
  // The query that produced the current results — its predicate (filter+extent)
  // drives the result table's match-first ordering, and its CQL the map
  // highlight. Captured on a successful Get Results.
  const [activeQuery, setActiveQuery] = useState<{
    filter: Record<string, any>;
    extent: ExtentPolygon | null;
    cql: string;
  } | null>(null);
  const queryLayerTitle = useMemo(() => drawerItems.find((l: any) => l.uniqueLayerName === layerName)?.layerTitle, [drawerItems, layerName]);

  const { fetchFeaturesKeyAndValueMapping, queryMapFeatures, fetchLayersInExtent, getLayerSymbologyFields, getLayerBounds } = MapPortalService();
  const { mutateAsync: queryFeatures, isPending: isQuerying } = useMutation(queryMapFeatures());
  const { mutateAsync: getLayerBoundsAsync } = useMutation(getLayerBounds());
  const { mutateAsync: getLayersInExtent, isPending: extentLayersLoading } = useMutation(fetchLayersInExtent());
  const { data: featuresKeyResponse, isFetching: isFeaturesFetching } = useQuery(fetchFeaturesKeyAndValueMapping(layerName));
  const { data: fieldsResponse } = useQuery(getLayerSymbologyFields(layerName));
  const layerSchema = useMemo(() => fieldsResponse?.fields || [], [fieldsResponse]);

  const { setState } = useLayerProperitesContext();

  // US-103163: search extent control. The polygon is drawn directly on the
  // Cesium viewer; the docked panel stays visible (the map is interactive
  // beside it) and the extent persists across query-config changes.
  const { isDrawing, beginDraw, cancelDraw, clearExtent } = useQueryExtentDraw({
    viewerRef,
    onComplete: async (polygon) => {
      setExtentPolygon(polygon);
      // Narrow the WITHIN dropdown to layers that have data inside the polygon.
      try {
        const names = await getLayersInExtent({
          layerNames: drawerItems.map((l: any) => l.uniqueLayerName),
          extent: polygon,
        });
        setExtentLayerNames(names);
        // If the selected layer no longer has data in the extent, reset it.
        if (layerName && !names.includes(layerName)) {
          setLayerName('');
          setFilters([{ category: '', condition: '', value: '', logicalOperator: '' }]);
        }
      } catch {
        // On failure, leave the dropdown unfiltered rather than blocking.
        setExtentLayerNames(null);
      }
    },
    onCancel: () => {
      // Aborted before closing a polygon -> fall back to the All extent.
      setExtentPolygon(null);
      setExtentLayerNames(null);
      setSearchExtent('all');
    },
  });

  const handleSelectAll = () => {
    setSearchExtent('all');
    // Stop any in-progress drawing and remove the committed extent: with All
    // selected, drawing must not be allowed (US-103163 Scenario 3 / 11).
    cancelDraw();
    clearExtent();
    setExtentPolygon(null);
    setExtentLayerNames(null);
  };

  const handleSelectPolygon = () => {
    setSearchExtent('polygon');
    clearExtent();
    setExtentPolygon(null);
    setExtentLayerNames(null);
    // Disable any other active map tool so its click handler can't steal our
    // drawing clicks, then enter drawing mode immediately (Scenario 4).
    setCurrentTool?.('');
    beginDraw();
  };

  // Layers offered in the WITHIN dropdown: all of them, unless a polygon extent
  // is active — then only layers with features inside it (US-103163).
  const withinLayers =
    searchExtent === 'polygon' && extentPolygon && extentLayerNames
      ? drawerItems.filter((l: any) => extentLayerNames.includes(l.uniqueLayerName))
      : drawerItems;

  useEffect(() => {
    if (!featuresKeyResponse) return;
    setFeatures(featuresKeyResponse);
    const keys = Object.keys(featuresKeyResponse).map((key) => ({
      label: key,
      value: key,
    }));
    setKeyOptions(keys);
  }, [featuresKeyResponse]);

  // Clear stale validation/execution errors as soon as the user edits the
  // configuration, so messages don't linger after the issue is fixed.
  useEffect(() => {
    setLayerError('');
    setRowErrors({});
    setSubmitError('');
  }, [filters, layerName]);

  const handleAddFilter = () => {
    setFilters([...filters, { category: '', condition: '', value: '', logicalOperator: '' }]);
  };

  const handleRemoveFilter = (index: number) => {
    const updatedFilters = filters.filter((_, i) => i !== index);
    setFilters(updatedFilters);
  };

  const handleCategoryChange = (value: string, index: number) => {
    const updatedFilters = [...filters];
    // Changing the field resets that row's dependent condition/value so it
    // can't carry over a now-invalid selection.
    updatedFilters[index].category = value;
    updatedFilters[index].condition = '';
    updatedFilters[index].value = '';
    setFilters(updatedFilters);
  };

  const handleConditionChange = (value: string, index: number) => {
    const updatedFilters = [...filters];
    updatedFilters[index].condition = value;
    setFilters(updatedFilters);
  };

  const handleValueChange = (value: string, index: number) => {
    const updatedFilters = [...filters];
    updatedFilters[index].value = value;
    setFilters(updatedFilters);
  };

  const handleLogicalOperatorChange = (value: string, index: number) => {
    const updatedFilters = [...filters];
    updatedFilters[index].logicalOperator = value;
    setFilters(updatedFilters);
  };

  // Reset the entire query configuration back to a fresh state (fields, extent,
  // errors, results and the map highlight). Shared by the result table's Reset
  // Query action and by closing the panel, so re-opening always starts fresh.
  const resetConfig = () => {
    setFilters([{ category: '', condition: '', value: '', logicalOperator: '' }]);
    setFeatures({});
    setKeyOptions([]);
    setLayerName('');
    setFilteredItems([]);
    setMatchTotal(0);
    setLayerError('');
    setRowErrors({});
    setSubmitError('');
    setSearchExtent('all');
    setExtentPolygon(null);
    setExtentLayerNames(null);
    cancelDraw();
    clearExtent();
    clearQueryHighlight();
  };

  const handleClear = () => {
    resetConfig();
    setQueryResultModalVisible(false);
    setQueryModalToolVisible(true);
  };

  // Escape a literal value so a dropdown selection used with LIKE/NOT LIKE is
  // matched as a substring, not interpreted as a regular expression.
  const escapeRegex = (v: string) => String(v).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Map a single configured row to a sift condition.
  const toCondition = (category: string, condition: string, value: string): Record<string, any> => {
    // A user-selected NULL from the VALUE dropdown arrives as the NULL_VALUE
    // sentinel (see comment at the top of the file) — unwrap it to a real
    // `null` here, at the point the actual query condition is built.
    const isNullValue = value === NULL_VALUE;
    const isNumeric = !isNullValue && !isNaN(parseFloat(value)) && isFinite(Number(value));
    const val = isNullValue ? null : isNumeric ? Number(value) : value;

    switch (condition) {
      case 'IS':
        return { [category]: val };
      case 'IS NOT':
        return { [category]: { $ne: val } };
      case 'LIKE':
        return { [category]: { $regex: escapeRegex(value), $options: 'i' } };
      case 'NOT LIKE':
        return { [category]: { $not: { $regex: escapeRegex(value), $options: 'i' } } };
      case 'GT':
        return { [category]: { $gt: val } };
      case 'GTE':
        return { [category]: { $gte: val } };
      case 'LT':
        return { [category]: { $lt: val } };
      case 'LTE':
        return { [category]: { $lte: val } };
      case 'EMPTY':
        return { [category]: null };
      case 'NOT_EMPTY':
        return { [category]: { $ne: null } };
      default:
        return {};
    }
  };

  // Left-associative grouping per US-103168 Scenario 10:
  // A and B or C -> (A and B) or C; the operator on row N joins the
  // accumulated group with row N.
  const buildQuery = (filters: Filter[]): Record<string, any> =>
    filters.reduce((acc: Record<string, any>, filter: Filter, index: number) => {
      const { category, condition, value, logicalOperator } = filter;
      if (!category || !condition) return acc;

      // Bypass value check for Empty states.
      // Note: a NULL_VALUE selection is a non-empty truthy string, so it naturally passes this guard without needing a separate check.
      const needsValue = condition !== 'EMPTY' && condition !== 'NOT_EMPTY';
      if (needsValue && (value === undefined || value === null || value === '')) return acc;

      const filterCondition = toCondition(category, condition, value);
      if (index === 0) return filterCondition;
      const operator: '$and' | '$or' = logicalOperator.toLowerCase() === 'and' ? '$and' : '$or';
      return { [operator]: [acc, filterCondition] };
    }, {});

  // Build a CQL_FILTER for the map highlight (US-103191). This scales to
  // millions because it's the predicate itself — no enumerated gid list.
  const buildMatchCql = (filters: Filter[], extent: ExtentPolygon | null): string => {
    const lit = (v: any) => (typeof v === 'number' && Number.isFinite(v) ? String(v) : `'${String(v).replace(/'/g, "''")}'`);
    const like = (v: any) => `'%${String(v).replace(/'/g, "''")}%'`;
    const cond = (f: Filter): string | null => {
      if (!f.category || !f.condition) return null;
      switch (f.condition) {
        case 'IS':
          return f.value === NULL_VALUE ? `${f.category} IS NULL` : `${f.category} = ${lit(f.value)}`;
        case 'IS NOT':
          return f.value === NULL_VALUE ? `${f.category} IS NOT NULL` : `${f.category} <> ${lit(f.value)}`;
        case 'LIKE':
          return `${f.category} ILIKE ${like(f.value)}`;
        case 'NOT LIKE':
          return `NOT (${f.category} ILIKE ${like(f.value)})`;
        case 'GT':
          return `${f.category} > ${lit(f.value)}`;
        case 'GTE':
          return `${f.category} >= ${lit(f.value)}`;
        case 'LT':
          return `${f.category} < ${lit(f.value)}`;
        case 'LTE':
          return `${f.category} <= ${lit(f.value)}`;
        case 'EMPTY':
          return `${f.category} IS NULL`;
        case 'NOT_EMPTY':
          return `${f.category} IS NOT NULL`;
        default:
          return null;
      }
    };
    let attr = '';
    filters.forEach((f) => {
      const c = cond(f);
      if (!c) return;
      attr = !attr ? c : f.logicalOperator?.toLowerCase() === 'or' ? `(${attr}) OR (${c})` : `(${attr}) AND (${c})`;
    });
    const parts: string[] = [];
    if (attr) parts.push(`(${attr})`);
    if (extent) {
      const ring = extent.coordinates?.[0] ?? [];
      const wkt = ring.map((p) => `${p[0]} ${p[1]}`).join(', ');
      if (wkt) parts.push(`INTERSECTS(geom, POLYGON((${wkt})))`);
    }
    return parts.join(' AND ');
  };

  const applyFilters = async (filters: Filter[], layername: string): Promise<{ features: any[]; total: number; hasMore: boolean }> => {
    const query = buildQuery(filters);

    // Page 0 only — full match set is never materialised client-side.
    // Attribute table paginates via /feature-table + matchFilter/matchExtent.
    const data = await queryFeatures({
      layername,
      queryfilter: query,
      // Only constrain by polygon when the Polygon extent is active and drawn.
      extent: searchExtent === 'polygon' ? extentPolygon : null,
      page: 0,
      pageSize: 500,
    });
    // Backward-compat: if an older API still returns a bare array, wrap it.
    if (Array.isArray(data)) {
      return { features: data, total: data.length, hasMore: false };
    }
    return {
      features: data?.features ?? [],
      total: Number(data?.total ?? 0),
      hasMore: Boolean(data?.hasMore),
    };
  };

  // US-103168 Scenarios 19-22: validate the configuration and surface inline
  // messages. Returns true when the query may execute.
  const validateQuery = (): boolean => {
    if (!layerName) {
      setLayerError('Layer selection is required.');
      return false;
    }
    const errors: Record<number, string> = {};
    filters.forEach((f, i) => {
      if (!f.category) errors[i] = 'Field selection is required.';
      else if (!f.condition) errors[i] = 'Condition is required.';
      else {
        // Note: a NULL_VALUE selection is a non-empty truthy string, so it
        // naturally passes this guard without needing a separate check.
        const needsValue = f.condition !== 'EMPTY' && f.condition !== 'NOT_EMPTY';
        if (needsValue && (f.value === undefined || f.value === null || f.value === '')) {
          errors[i] = 'Value is required.';
        }
      }
    });
    if (Object.keys(errors).length) {
      setRowErrors(errors);
      return false;
    }
    return true;
  };

  const handleApply = async (): Promise<void> => {
    setSubmitError('');
    if (!validateQuery()) return;

    // Scenario 27: the layer may have been removed since it was selected.
    const layerStillExists = drawerItems.some((l: any) => l.uniqueLayerName === layerName);
    if (!layerStillExists) {
      setSubmitError('The selected layer is no longer available.');
      return;
    }

    try {
      const { features, total } = await applyFilters(filters, layerName);
      // Open the result table only on success — no partial results on failure.
      // The table opens in server mode; matches float to the top via the COMPACT
      // query predicate, and the map highlight is driven by the predicate CQL
      // (selectionCql) — both scale to millions without enumerating ids.
      setActiveQuery({
        filter: buildQuery(filters),
        extent: searchExtent === 'polygon' ? extentPolygon : null,
        cql: buildMatchCql(filters, searchExtent === 'polygon' ? extentPolygon : null),
      });
      // The result table publishes selectionCql from matchCql; this only colours it.
      setState((prev) => ({ ...prev, highlightColor: settings?.queryTool?.color ?? null }));
      setFilteredItems?.(features);
      setMatchTotal(total);
      setQueryResultModalVisible(true);
      setQueryModalToolVisible(false);
      setTimeout(async () => {
        try {
          const bounds = await getLayerBoundsAsync({ layerName });
          if (bounds && viewerRef.current) {
            const minX = parseFloat(bounds.minX || bounds.minx);
            const minY = parseFloat(bounds.minY || bounds.miny);
            const maxX = parseFloat(bounds.maxX || bounds.maxx);
            const maxY = parseFloat(bounds.maxY || bounds.maxy);

            if (!isNaN(minX) && (minX !== 0 || minY !== 0)) {
              viewerRef.current.camera.flyTo({
                destination: Cesium.Rectangle.fromDegrees(minX, minY, maxX, maxY),
                duration: 1.5,
              });
            }
          }
        } catch (boundsErr) {
          console.warn('Auto-zoom failed:', boundsErr);
        }
      }, 1000);
    } catch (err: any) {
      // Scenario 26/27: keep the configuration intact and report the failure.
      setSubmitError(err?.response?.status === 404 ? 'The selected layer is no longer available.' : 'Unable to execute query. Please try again.');
    }
  };

  // Remove the query result highlight/graphics from the map.
  const clearQueryHighlight = () => {
    setState((prev: any) => ({ ...prev, selectedFeatureIds: [], selectionCql: null, highlightColor: null }));
    setActiveQuery(null);
  };

  const handleCancel = () => {
    // Closing the panel mid-draw must also stop drawing (no orphaned banner),
    // and reset the query result highlight on the map.
    if (isQuerying) return; //additional guard
    cancelDraw();
    clearExtent();
    clearQueryHighlight();
    // Closing the panel discards the whole configuration so re-opening the tool
    // starts fresh (no retained layer/filters), stops any in-progress drawing
    // (no orphaned banner) and clears the map highlight.
    resetConfig();
    setQueryModalToolVisible(false);
  };

  const handleLayerChange = async (value: string) => {
    setLayerName(value);
    // US-103168 Scenario 18: changing the layer clears all configured
    // conditions — fields from the old layer don't apply to the new one.
    setFilters([{ category: '', condition: '', value: '', logicalOperator: '' }]);
    // Clear stale options from the previous layer immediately so the category
    // dropdown doesn't show wrong fields while the new layer's data is loading.
    setKeyOptions([]);
    setFeatures({});
    const selectedLayer = drawerItems.find((layer: any) => layer.uniqueLayerName === value);
    if (selectedLayer) {
      setState(() => ({
        ...defaultLayerPropertiesState,
        ...Object.fromEntries(Object.entries(selectedLayer).filter(([_, v]) => v !== null && v !== undefined)),
        isExistingLayer: true,
      }));
    }
  };

  const handleFeatureChange = async (value: string, index: number) => {
    handleValueChange(value, index);
  };

  return (
    <>
      {isDrawing && (
        <div className="query-tool__draw-banner">
          Click the starting point to finish drawing.
          <span className="query-tool__draw-banner-hint">Right-click to cancel.</span>
        </div>
      )}

      {queryModalToolVisible && (
        // US-103163: docked panel (matches Figma) — floats over the map at the
        // bottom-left, leaving the map interactive for polygon drawing.
        <div className={`query-panel ${isQuerying ? 'query-panel--disable' : ''}`}>
          <div className="query-panel__head">
            <span className="query-panel__title">{settings?.queryTool?.name?.trim() || 'Query Tool'}</span>
            <button type="button" aria-label="Close" className="query-panel__close" onClick={handleCancel}>
              ×
            </button>
          </div>

          {/* Search Extent control — All vs Polygon (mutually exclusive,
              All by default). */}
          <div className="query-tool__extent">
            <button type="button" className={`query-tool__extent-btn ${searchExtent === 'all' ? 'is-active' : ''}`} onClick={handleSelectAll}>
              ALL
            </button>
            <button
              type="button"
              aria-label="Draw polygon search extent"
              title="Draw polygon search extent"
              className={`query-tool__extent-btn query-tool__extent-btn--icon ${searchExtent === 'polygon' ? 'is-active' : ''}`}
              onClick={handleSelectPolygon}
            >
              <svg width="23" height="18" viewBox="0 0 23 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M3.91708 1.54953L8.69584 5.08935L21.7932 1.01855V8.80617L12.9436 16.7708L0.908234 12.346L3.91708 1.54953Z"
                  stroke={searchExtent === 'polygon' ? '#ffffff' : '#333333'}
                  stroke-width="2"
                  stroke-miterlimit="10"
                />
              </svg>
            </button>
          </div>

          <div className="query-panel__row">
            <span className="query-tool__label">WITHIN</span>
            <Select
              suffixIcon={SELECT_CARET}
              popupClassName="query-tool__dropdown"
              placeholder="select layer"
              className="query-tool__within-select"
              value={layerName || undefined}
              loading={extentLayersLoading}
              showSearch
              onChange={(value) => handleLayerChange(value)}
              notFoundContent={searchExtent === 'polygon' && extentPolygon ? 'No layers in the drawn extent' : undefined}
            >
              {/* US-101376 AC-06: a layer that's hidden from the legend
                  (displayInLegend === false) must still be selectable by the
                  Query tool. The hidden-from-legend / hidden-from-tool
                  decisions are independent — only the Toolbox toggle should
                  gate tool access.
                  US-103163: when a polygon extent is active, only layers with
                  features inside it are offered (withinLayers). */}
              {/* US-103168 Scenario 1: show the layer's legend name
                  (layerTitle), falling back to the backend uniqueLayerName. */}
              {withinLayers?.map((layer: any, index: number) => (
                <Option key={index} value={layer.uniqueLayerName}>
                  {layer.layerTitle || layer.uniqueLayerName}
                </Option>
              ))}
            </Select>
          </div>
          {layerError && <div className="query-tool__error query-tool__error--layer">{layerError}</div>}

          {filters.map((filter, index) => (
            <FilterItem
              key={index}
              filter={filter}
              index={index}
              keyOptions={keyOptions}
              features={features}
              layerName={layerName}
              error={rowErrors[index]}
              optionsLoading={isFeaturesFetching}
              handleCategoryChange={handleCategoryChange}
              handleConditionChange={handleConditionChange}
              handleValueChange={handleValueChange}
              handleLogicalOperatorChange={handleLogicalOperatorChange}
              handleRemoveFilter={handleRemoveFilter}
              setDrawerItems={setDrawerItems}
              handleFeatureChange={handleFeatureChange}
              schema={layerSchema}
              loading={isFeaturesFetching}
            />
          ))}

          <div className="query-panel__add-wrap">
            <button type="button" className="query-panel__add" onClick={handleAddFilter}>
              ADD
            </button>
          </div>

          {submitError && <div className="query-tool__error query-tool__error--submit">{submitError}</div>}

          <div className="query-panel__footer">
            {/* US-103168 Scenario 25: disable while a query runs to prevent
                duplicate executions; show a loading indicator. */}
            <button type="button" className="query-panel__results" disabled={isQuerying} onClick={handleApply}>
              {isQuerying ? <Spin size="small" /> : 'Get Results'}
            </button>
          </div>
        </div>
      )}

      {queryResultModalVisible && (
        <ViewAttributeTable
          currentMapId={mapId}
          isVisible={queryResultModalVisible}
          layerName={layerName}
          layerTitle={queryLayerTitle}
          seedSelectionIds={seedSelectionIds}
          seedMatchTotal={matchTotal}
          seedSelectAllMatches={seedSelectAllMatches}
          matchFilter={activeQuery?.filter}
          matchExtent={activeQuery?.extent ?? null}
          matchCql={activeQuery?.cql ?? null}
          handleClose={() => {
            resetConfig();
            setMatchTotal(0);
            setQueryResultModalVisible(false);
          }}
          isQueryTool
          onEditQuery={() => {
            setQueryResultModalVisible(false);
            setQueryModalToolVisible(true);
            setState((prev) => ({
              ...prev,
              selectedFeatureIds: [],
              selectionCql: null,
              highlightColor: null,
            }));
          }}
          onResetQuery={handleClear}
          setIsLayerLoading={setIsLayerLoading} // Loading State in map
          onLayerUpdateComplete={onLayerUpdateComplete} // Loading State in map
          canEdit={canSaveResults}
        />
      )}
    </>
  );
};

export default QueryModal;

interface FilterItemProps {
  filter?: Filter;
  index: number;
  keyOptions: any[];
  // Query Tool path: the full field -> distinct-values map, so each row derives
  // its OWN value options from its OWN selected field (correct for multi-row).
  features?: Record<string, any[]>;
  // Legacy path (LabelsTab): a pre-computed shared value list. Used only when
  // `features` is not supplied.
  valueOptions?: any[];
  selectedKey?: string;
  layerName: string;
  error?: string;
  optionsLoading?: boolean;
  handleCategoryChange: (value: string, index: number) => void;
  handleConditionChange: (value: string, index: number) => void;
  handleValueChange: (value: string, index: number) => void;
  handleFeatureChange: (value: string, index: number) => void;
  handleLogicalOperatorChange: (value: string, index: number) => void;
  handleRemoveFilter: (index: number) => void;
  setDrawerItems: React.Dispatch<React.SetStateAction<any[]>>;
  schema?: any;
  // True while this row's field/value options are being fetched from the API.
  loading?: boolean;
}

export const FilterItem: React.FC<FilterItemProps> = ({
  filter,
  index,
  keyOptions,
  features,
  // valueOptions: valueOptionsProp,
  layerName,
  error,
  optionsLoading,
  handleCategoryChange,
  handleConditionChange,
  handleLogicalOperatorChange,
  handleRemoveFilter,
  // handleFeatureChange,
  handleValueChange,
  schema,
}) => {
  // Prefer per-row values derived from this row's own field (Query Tool); fall
  // back to the shared list passed by legacy callers (LabelsTab).
  // const _valueOptions = features
  //   ? (features[filter?.category ?? ""] ?? [])
  //       .filter((val: any) => val !== undefined && val !== null)
  //       .map((val: any) => ({ label: String(val), value: val }))
  //   : valueOptionsProp ?? [];

  const isNumeric = schema?.find((f: any) => f.name === filter?.category)?.numeric;
  const filteredOperators = OPERATORS.filter((op) => {
    if (op.numericOnly && !isNumeric) return false;
    return true;
  });

  const valueOptions = features ? (features[filter?.category ?? ''] ?? []) : [];

  const hasCategory = Boolean(filter?.category);
  // Scenario 23/24: Hide value field for "Empty" checks
  const showValueField = filter?.condition !== 'EMPTY' && filter?.condition !== 'NOT_EMPTY';

  // Scenario 4: Use Input for "Contains" (LIKE)
  const isSearchable = filter?.condition === 'LIKE' || filter?.condition === 'NOT LIKE';
  const loadingContent = (
    <div className="query-tool--field-loading">
      <Spin size="small" />
    </div>
  );

  return (
    <>
      <div key={index} className="query-tool__filter-row">
        {index === 0 && <span className="query-tool__label">SELECT</span>}
        {index !== 0 && (
          <Select
            suffixIcon={SELECT_CARET}
            popupClassName="query-tool__dropdown"
            className="query-tool__logical"
            placeholder="Logical"
            value={filter?.logicalOperator || undefined}
            onChange={(value) => handleLogicalOperatorChange(value, index)}
            popupMatchSelectWidth={false}
          >
            <Option value="AND">AND</Option>
            <Option value="OR">OR</Option>
          </Select>
        )}
        <Select
          suffixIcon={SELECT_CARET}
          popupClassName="query-tool__dropdown"
          className="query-tool__field"
          placeholder="Category"
          value={filter?.category || undefined}
          onChange={(value) => handleCategoryChange(value, index)}
          disabled={!layerName}
          notFoundContent={optionsLoading ? loadingContent : undefined}
          loading={optionsLoading}
          popupMatchSelectWidth={false}
        >
          {keyOptions?.map((key: any) => (
            <Option key={key.label} value={key.value}>
              {key.value}
            </Option>
          ))}
        </Select>
        <Select
          suffixIcon={SELECT_CARET}
          popupClassName="query-tool__dropdown"
          className="query-tool__field"
          placeholder="Condition"
          value={filter?.condition || undefined}
          onChange={(value) => handleConditionChange(value, index)}
          disabled={!hasCategory}
          popupMatchSelectWidth={false}
        >
          {filteredOperators.map((op) => (
            <Option key={op.value} value={op.value}>
              {op.label}
            </Option>
          ))}
        </Select>
        {showValueField &&
          (isSearchable ? (
            <Input
              className="query-tool__field"
              placeholder="TYPE KEYWORD..."
              value={filter?.value || ''}
              onChange={(e) => handleValueChange(e.target.value, index)}
            />
          ) : (
            <Select
              suffixIcon={SELECT_CARET}
              popupClassName="query-tool__dropdown"
              className="query-tool__field"
              placeholder="VALUE"
              showSearch
              value={filter?.value || undefined}
              onChange={(v) => handleValueChange(v, index)}
              disabled={!filter?.category}
              notFoundContent={optionsLoading ? loadingContent : undefined}
              loading={optionsLoading}
              popupMatchSelectWidth={false}
            >
              {valueOptions?.map(
                (val: any) =>
                  val ? (
                    <Option key={String(val)} value={val}>
                      {/* Keep raw `value` for query matching; format date timestamps for display. */}
                      {formatDisplayDate(val)}
                    </Option>
                  ) : (
                    <Option value={NULL_VALUE}>NULL</Option>
                  ), // Scenario 4: Include NULL if it exists in data.
              )}
            </Select>
          ))}
        {index !== 0 && (
          <div className="query-tool__remove" onClick={() => handleRemoveFilter(index)}>
            X
          </div>
        )}
      </div>
      {error && <div className="query-tool__error query-tool__error--row">{error}</div>}
    </>
  );
};
